import { Link } from "@tanstack/react-router"
import { Textarea } from "design-system/atoms/Textarea"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import InstagramAccountFormSelector from "modules/campaigns/components/Sections/triggers/instagram/InstagramAccountFormSelector"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { z } from "zod"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type IgComment = Extract<
  NonNullable<FormValues["triggerIntegration"]>,
  { type: "instagramCommentReply" }
>

export function InstagramCommentReplyForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const igComment =
    triggerIntegration?.type === "instagramCommentReply" ? triggerIntegration : undefined

  // Rebuild the full variant on every edit so `type` and the account id survive partial changes.
  const buildIgComment = (patch: Partial<IgComment>): IgComment => ({
    type: "instagramCommentReply",
    instagramIntegrationId: igComment?.instagramIntegrationId ?? "",
    replies: igComment?.replies ?? undefined,
    allowList: igComment?.allowList ?? undefined,
    ...patch,
  })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-2">
              <FormLabel>
                {t("campaigns.trigger.metadata.instagramCommentReply.selectAccount")}
              </FormLabel>
              <FormControl>
                <InstagramAccountFormSelector
                  value={igComment?.instagramIntegrationId}
                  emptyPlaceholder={t(
                    "campaigns.trigger.metadata.instagramCommentReply.emptyPlaceholder",
                  )}
                  placeholder={t(
                    "campaigns.trigger.metadata.instagramCommentReply.emptyPlaceholder",
                  )}
                  searchPlaceholder={t(
                    "campaigns.trigger.metadata.instagramCommentReply.searchPlaceholder",
                  )}
                  onChange={(account) => {
                    field.onChange(
                      account ? buildIgComment({ instagramIntegrationId: account.value }) : null,
                      { shouldDirty: true },
                    )
                  }}
                  hasCreateNew
                  isDisabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
              <span className="text-sm text-muted-foreground">
                <Trans
                  i18nKey={"campaigns.trigger.metadata.instagramCommentReply.accountDescription"}
                  components={{
                    a: (
                      <Link
                        className="underline text-muted-foreground"
                        to="/settings/channels/instagram"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </span>
            </FormItem>
          </div>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-1">
              <FormControl>
                <div className="flex flex-col gap-4">
                  <span className="text-muted-foreground text-xs block whitespace-pre-line">
                    <Trans
                      i18nKey={"campaigns.trigger.metadata.instagramCommentReply.reply.explanation"}
                      components={{
                        b: <span className="font-medium underline underline-offset-2" />,
                      }}
                    />
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground text-xs font-medium">
                      {t("campaigns.trigger.metadata.instagramCommentReply.reply.optionsTitle")}
                    </span>
                    <Textarea
                      onKeyDown={(ev) => ev.stopPropagation()}
                      disabled={isDisabled}
                      placeholder={t(
                        "campaigns.trigger.metadata.instagramCommentReply.reply.description",
                      )}
                      value={igComment?.replies?.join("\n") ?? ""}
                      onChange={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        field.onChange(buildIgComment({ replies: ev.target.value.split("\n") }), {
                          shouldDirty: true,
                        })
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground text-xs font-medium">
                      {t("campaigns.trigger.metadata.instagramCommentReply.allowList.title")}
                    </span>
                    <Textarea
                      onKeyDown={(ev) => ev.stopPropagation()}
                      disabled={isDisabled}
                      placeholder={t(
                        "campaigns.trigger.metadata.instagramCommentReply.allowList.placeholder",
                      )}
                      value={igComment?.allowList?.join("\n") ?? ""}
                      onChange={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        field.onChange(buildIgComment({ allowList: ev.target.value.split("\n") }), {
                          shouldDirty: true,
                        })
                      }}
                    />
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
    </>
  )
}
